import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Spinner,
} from "@fluentui/react-components";
import { MoreVerticalRegular, DismissRegular } from "@fluentui/react-icons";
import { ErrorBadgeIcon, RefreshIcon } from "@fluentui/react-icons-mdl2";
import {
  ReceiptMoneyRegular,
  DocumentCheckmarkRegular,
  BugRegular,
  ArrowUndoRegular,
} from "@fluentui/react-icons";
import { useSetAtom } from "jotai";
import { OfferAzureSearch } from "library/types/types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom"; // Updated import
import {
  offersPageInvalidateOfferDialogState,
  offersPageMarkOfferAsErrorDialogState,
} from "store/OffersPageStore";
import {
  useMarkInvoiceAsPaidMutation,
  useMarkPaidInvoiceAsIssuedMutation,
} from "state/queries/useOffers";
import { useLoadOffers } from "store/ProposalsPageHooks";
import { useState } from "react";
import { useTaxonomy } from "library/utils/useTaxonomy";
import api from "service/api";
import { helpAnchor } from "help/anchor";
import { useHasAnyRole } from "hooks/useUserRoles";
import { RolePermissions } from "library/constants/roles";

type Props = {
  offer?: OfferAzureSearch;
};

const MENU_ITEM_ANCHORS: Record<string, string> = {
  repeatOffer: helpAnchor("offers-row-menu-duplicate"),
  markInvoiceAsPaid: helpAnchor("offers-row-menu-mark-paid"),
  markGroupInvoiceAsPaid: helpAnchor("offers-row-menu-mark-group-paid"),
  markPaidInvoiceAsIssued: helpAnchor("offers-row-menu-mark-issued"),
  invalidateOffer: helpAnchor("offers-row-menu-invalidate"),
  markAsError: helpAnchor("offers-row-menu-mark-as-error"),
  unmarkAsError: helpAnchor("offers-row-menu-unmark-as-error"),
};

export const ContextButton = ({ offer }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate(); // Updated hook
  const loadOffers = useLoadOffers();
  const taxonomy = useTaxonomy();
  const setInvalidateOfferDialog = useSetAtom(
    offersPageInvalidateOfferDialogState
  );
  const setMarkOfferAsErrorDialog = useSetAtom(
    offersPageMarkOfferAsErrorDialogState
  );
  const canMarkAsError = useHasAnyRole(RolePermissions.canMarkOffersAsError());
  // Mark as paid dialog: "line" - the offer's own invoice only, "group" - the whole group invoice, null - closed
  const [markAsPaidMode, setMarkAsPaidMode] = useState<"line" | "group" | null>(
    null,
  );
  const [isMarkAsIssuedDialogOpen, setIsMarkAsIssuedDialogOpen] =
    useState(false);

  // Get offer status code from taxonomy
  const offerStatusCode =
    taxonomy?.OfferStatus?.byId[offer?.OfferStatus || 0]?.code;

  // Check if offer has invoice status (INVOICE or INVOICE_SENT)
  const hasInvoiceStatus =
    offerStatusCode === "OfferStatus.INVOICE" ||
    offerStatusCode === "OfferStatus.INVOICE_SENT";

  // The offer's invoice is part of a group invoice that is not paid yet (GROUP_INVOICE or GROUP_INVOICE_SENT)
  const hasGroupInvoiceStatus =
    offerStatusCode === "OfferStatus.GROUP_INVOICE" ||
    offerStatusCode === "OfferStatus.GROUP_INVOICE_SENT";

  // Check if offer has paid invoice status (INVOICE_PAID or GROUP_INVOICE_PAID)
  const hasInvoicePaidStatus =
    offerStatusCode === "OfferStatus.INVOICE_PAID" ||
    offerStatusCode === "OfferStatus.GROUP_INVOICE_PAID";

  const markInvoiceAsPaidMutation = useMarkInvoiceAsPaidMutation(() => {
    setMarkAsPaidMode(null);
    loadOffers({
      skip: 0,
      top: 40,
      reset: true,
    });
  });

  const markPaidInvoiceAsIssuedMutation = useMarkPaidInvoiceAsIssuedMutation(
    () => {
      setIsMarkAsIssuedDialogOpen(false);
      loadOffers({
        skip: 0,
        top: 40,
        reset: true,
      });
    },
  );

  if (!offer) return null;

  const onClickInvalidateOffer = () => {
    setInvalidateOfferDialog({
      isOpen: true,
      offer,
    });
  };

  const onClickMarkAsError = (markAsError: boolean) => {
    setMarkOfferAsErrorDialog({
      isOpen: true,
      offer,
      markAsError,
    });
  };

  const onClickRepeatOffer = () => {
    navigate("/offer?duplicate=" + offer.OfferId); // Updated navigation
  };

  const handleMarkAsPaidConfirm = async () => {
    // Both endpoints match the invoice by number, which the search row carries - no need to load the offer.
    // "group" settles all unpaid offers of the group invoice; "line" marks only this offer's own invoice
    // (also for a group invoice member, which then leaves the group as Invoice Paid) - prefer the one not paid yet.
    const isGroupMode = markAsPaidMode === "group";
    let invoiceNumber: string | null | undefined = isGroupMode
      ? offer.GroupInvoiceNumber
      : (offer.InvoiceMtplIsPaid === false ? offer.InvoiceMtplInvoiceNumber : null) ||
        (offer.InvoiceCascoIsPaid === false ? offer.InvoiceCascoInvoiceNumber : null) ||
        offer.InvoiceMtplInvoiceNumber ||
        offer.InvoiceCascoInvoiceNumber;

    if (isGroupMode && !invoiceNumber && offer.OfferId) {
      // the search row has no GroupInvoice fields yet (index not refreshed): read the number from the offer itself
      try {
        const response = await api.get(`/offers/${offer.OfferId}`);
        invoiceNumber =
          response.data?.Invoice?.GroupInvoice?.Request?.InvoiceNumber || null;
      } catch {
        invoiceNumber = null;
      }
    }

    if (offer.OfferId && invoiceNumber) {
      markInvoiceAsPaidMutation.mutate({
        offerId: offer.OfferId,
        invoiceNumber,
      });
    }
  };

  const onClickMarkPaidInvoiceAsIssued = () => {
    setIsMarkAsIssuedDialogOpen(true);
  };

  const handleMarkAsIssuedConfirm = () => {
    // Prefer the invoice that is actually marked as paid (Casco or Mtpl) - numbers come from the search row
    const invoiceNumber =
      (offer.InvoiceCascoIsPaid ? offer.InvoiceCascoInvoiceNumber : null) ||
      (offer.InvoiceMtplIsPaid ? offer.InvoiceMtplInvoiceNumber : null) ||
      offer.InvoiceCascoInvoiceNumber ||
      offer.InvoiceMtplInvoiceNumber;

    if (offer.OfferId && invoiceNumber) {
      markPaidInvoiceAsIssuedMutation.mutate({
        offerId: offer.OfferId,
        invoiceNumber,
      });
    }
  };

  // Invalidate is only enabled if both SelectedInsurerCasco and SelectedInsurerMtpl are null
  // and the invoice (if any) is not sent or paid - status INVOICE can still be invalidated
  const canInvalidate =
    !offer.SelectedInsurerCasco &&
    !offer.SelectedInsurerMtpl &&
    offerStatusCode !== "OfferStatus.INVOICE_SENT" &&
    offerStatusCode !== "OfferStatus.INVOICE_PAID" &&
    !hasGroupInvoiceStatus &&
    offerStatusCode !== "OfferStatus.GROUP_INVOICE_PAID";

  // Mark (the offer's own) invoice as paid is enabled if the offer has an invoice status - single or unpaid group -
  // and at least one of its invoices is not paid yet
  const canMarkAsPaid =
    (hasInvoiceStatus || hasGroupInvoiceStatus) &&
    (offer.InvoiceMtplIsPaid === false || offer.InvoiceCascoIsPaid === false);

  // Mark group invoice as paid settles every offer of the group invoice that is not paid yet
  const canMarkGroupAsPaid =
    hasGroupInvoiceStatus && offer.GroupInvoiceIsPaid !== true;

  // an offer marked as error allows no other operation than unmarking it (admins only)
  const isMarkedAsError = offer.IsMarkedAsError === true;

  const errorItems = canMarkAsError
    ? [
        {
          key: isMarkedAsError ? "unmarkAsError" : "markAsError",
          text: isMarkedAsError
            ? t("qtg.contextMenu.unmarkAsError", "Unmark Error")
            : t("qtg.contextMenu.markAsError", "Mark as Error"),
          iconProps: isMarkedAsError ? <ArrowUndoRegular /> : <BugRegular />,
          disabled: false,
          onClick: () => onClickMarkAsError(!isMarkedAsError),
          hidden: false,
        },
      ]
    : [];

  const operationItems = [
    {
      key: "repeatOffer",
      text: t("qtg.contextMenu.duplicate"),
      iconProps: <RefreshIcon />,
      disabled: false,
      onClick: onClickRepeatOffer,
    },
    {
      key: "markInvoiceAsPaid",
      text: t("qtg.contextMenu.markInvoiceAsPaid", "Mark Invoice as Paid"),
      iconProps: <ReceiptMoneyRegular />,
      disabled: !canMarkAsPaid,
      onClick: () => setMarkAsPaidMode("line"),
      hidden: !hasInvoiceStatus && !hasGroupInvoiceStatus,
    },
    {
      key: "markGroupInvoiceAsPaid",
      text: t(
        "qtg.contextMenu.markGroupInvoiceAsPaid",
        "Mark Group Invoice as Paid",
      ),
      iconProps: <ReceiptMoneyRegular />,
      disabled: !canMarkGroupAsPaid,
      onClick: () => setMarkAsPaidMode("group"),
      hidden: !hasGroupInvoiceStatus,
    },
    {
      key: "markPaidInvoiceAsIssued",
      text: t(
        "qtg.contextMenu.markPaidInvoiceAsIssued",
        "Mark Paid Invoice as Issued",
      ),
      iconProps: <DocumentCheckmarkRegular />,
      disabled: !hasInvoicePaidStatus,
      onClick: onClickMarkPaidInvoiceAsIssued,
      hidden: !hasInvoicePaidStatus,
    },
    {
      key: "invalidateOffer",
      text: t("qtg.contextMenu.invalidate"),
      iconProps: <ErrorBadgeIcon />,
      disabled: !canInvalidate,
      onClick: onClickInvalidateOffer,
    },
  ];

  const items = isMarkedAsError
    ? errorItems
    : [...operationItems, ...errorItems];

  if (items.length === 0) return null;
  return (
    <>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <MenuButton
            data-help="offers-row-menu"
            appearance="transparent"
            icon={<MoreVerticalRegular />}
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {items
              .filter((item) => !item.hidden)
              .map((item) => (
                <MenuItem
                  key={item.key}
                  data-help={MENU_ITEM_ANCHORS[item.key]}
                  onClick={item.onClick}
                  disabled={item.disabled}
                >
                  <div style={{ display: "flex" }}>
                    <div style={{ width: "20px" }}>{item.iconProps}</div>
                    <div>{item.text}</div>
                  </div>
                </MenuItem>
              ))}
          </MenuList>
        </MenuPopover>
      </Menu>

      {/* Mark Invoice as Paid / Mark Group Invoice as Paid Dialog */}
      <Dialog
        open={markAsPaidMode !== null}
        onOpenChange={(e, data) => !data.open && setMarkAsPaidMode(null)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="close"
                  icon={<DismissRegular />}
                  onClick={() => setMarkAsPaidMode(null)}
                  disabled={markInvoiceAsPaidMutation.isPending}
                />
              }
            >
              {markAsPaidMode === "group"
                ? t(
                    "qtg.markGroupInvoiceAsPaid.dialog.title",
                    "Mark Group Invoice as Paid",
                  )
                : t("qtg.markInvoiceAsPaid.dialog.title", "Mark Invoice as Paid")}
            </DialogTitle>
            <DialogContent>
              <p>
                {markAsPaidMode === "group"
                  ? t("qtg.markGroupInvoiceAsPaid.dialog.message", {
                      offerId: offer?.OfferId,
                    })
                  : t("qtg.markInvoiceAsPaid.dialog.message", {
                      offerId: offer?.OfferId,
                    })}
              </p>
              {markAsPaidMode === "line" && hasGroupInvoiceStatus && (
                <p style={{ marginTop: "1rem" }}>
                  {t("qtg.markInvoiceAsPaid.dialog.groupMemberNote")}
                </p>
              )}
              <p style={{ marginTop: "1rem" }}>
                {t("qtg.markInvoiceAsPaid.dialog.confirmation")}
              </p>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setMarkAsPaidMode(null)}
                disabled={markInvoiceAsPaidMutation.isPending}
              >
                {t("qtg.common.cancel")}
              </Button>
              <Button
                appearance="primary"
                onClick={handleMarkAsPaidConfirm}
                disabled={markInvoiceAsPaidMutation.isPending}
                icon={
                  markInvoiceAsPaidMutation.isPending ? (
                    <Spinner size="tiny" />
                  ) : undefined
                }
              >
                {markInvoiceAsPaidMutation.isPending
                  ? t("qtg.common.processing")
                  : t("qtg.markInvoiceAsPaid.dialog.confirm", "Mark as Paid")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {/* Mark Paid Invoice as Issued Dialog */}
      <Dialog
        open={isMarkAsIssuedDialogOpen}
        onOpenChange={(e, data) =>
          !data.open && setIsMarkAsIssuedDialogOpen(false)
        }
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="close"
                  icon={<DismissRegular />}
                  onClick={() => setIsMarkAsIssuedDialogOpen(false)}
                  disabled={markPaidInvoiceAsIssuedMutation.isPending}
                />
              }
            >
              {t(
                "qtg.markPaidInvoiceAsIssued.dialog.title",
                "Mark Paid Invoice as Issued",
              )}
            </DialogTitle>
            <DialogContent>
              <p>
                {t("qtg.markPaidInvoiceAsIssued.dialog.message", {
                  offerId: offer?.OfferId,
                })}
              </p>
              <p style={{ marginTop: "1rem" }}>
                {t("qtg.markPaidInvoiceAsIssued.dialog.confirmation")}
              </p>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setIsMarkAsIssuedDialogOpen(false)}
                disabled={markPaidInvoiceAsIssuedMutation.isPending}
              >
                {t("qtg.common.cancel")}
              </Button>
              <Button
                appearance="primary"
                onClick={handleMarkAsIssuedConfirm}
                disabled={markPaidInvoiceAsIssuedMutation.isPending}
                icon={
                  markPaidInvoiceAsIssuedMutation.isPending ? (
                    <Spinner size="tiny" />
                  ) : undefined
                }
              >
                {markPaidInvoiceAsIssuedMutation.isPending
                  ? t("qtg.common.processing")
                  : t(
                      "qtg.markPaidInvoiceAsIssued.dialog.confirm",
                      "Mark as Issued",
                    )}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};
